"use client";

import { LiveNamedSymbolIcon } from "@/components/common";
import { memo, useState } from "react";
import { X } from "lucide-react";
import { TableCell, TableRow } from "@/shadcn-components/ui/table";
import { Button } from "@/shadcn-components/ui/button";
import { Input } from "@/shadcn-components/ui/input";
import { DirectionStatusCell } from "@/components/common";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuLabel,
  ContextMenuSeparator,
  ContextMenuTrigger,
} from "@/shadcn-components/ui/context-menu";
import type { BulkCloseMode } from "@/vybe-trader-client/connection/client/VybeTraderClient";
import type { OpenedTrade } from "@/vybe-trader-client/opened-trades";
import { focusOpenTradeOnDesktopCharts } from "@/lib/chart-navigation/focusOpenTradeOnDesktopCharts";
import { OpenPositionLiveCells } from "./OpenPositionLiveCells";
import type { OpenPositionTableRowData } from "./types";
import {
  OPEN_POSITION_COL_WIDTHS,
  openPositionsTd,
} from "./openPositionsTableLayout";
import { DENSITY } from "@/lib/density-scale";
import { INVESTOR_READ_ONLY_MESSAGE } from "@/vybe-trader-client/account-information/hooks/useAccountInformation";

function formatOptionalTradePrice(price: number, digits: number) {
  return price > 0 ? price.toFixed(digits) : "-";
}

export interface OpenPositionTableRowProps {
  row: OpenPositionTableRowData;
  isSelected: boolean;
  actionLabel: string;
  hasCloseByCandidate: boolean;
  bulkCounts: Record<BulkCloseMode, number>;
  onCloseTrade: (trade: OpenedTrade, lots?: number) => void;
  onCloseBy: (trade: OpenedTrade) => void;
  onBulkClose: (mode: BulkCloseMode) => void;
  /** Open the SL/TP modify dialog for this position. */
  onModify: (trade: OpenedTrade) => void;
  /** Remove one existing level outright (no dialog). */
  onClearLevel: (trade: OpenedTrade, level: "SL" | "TP") => void;
  /** Investor (read-only) session: no close button, no trade verbs in the context menu, and the SL/TP
   *  cells only focus the chart (no placement mode). */
  readOnly?: boolean;
}

function OpenPositionTableRowInner({
  row,
  isSelected,
  actionLabel,
  hasCloseByCandidate,
  bulkCounts,
  onCloseTrade,
  onCloseBy,
  onBulkClose,
  onModify,
  onClearLevel,
  readOnly = false,
}: OpenPositionTableRowProps) {
  const { trade, tradeType, isMarket, isPending } = row;
  // Partial-close lots input (context menu). Empty = full volume.
  const [partialLotsInput, setPartialLotsInput] = useState("");

  const submitPartialClose = () => {
    const lots = parseFloat(partialLotsInput);
    if (!Number.isFinite(lots) || lots <= 0) return;
    onCloseTrade(trade, Math.min(lots, trade.lots));
    setPartialLotsInput("");
  };
  const isBuyTrade = tradeType.startsWith("Buy");
  const rowInteractionClass =
    "h-7 cursor-pointer hover:bg-accent transition-colors";
  const rowClassName = isSelected
    ? `${rowInteractionClass} bg-accent/60`
    : rowInteractionClass;

  return (
    <ContextMenu>
      <ContextMenuTrigger asChild>
        <TableRow
          className={rowClassName}
          onClick={() => focusOpenTradeOnDesktopCharts(trade)}
        >
          <TableCell className={`${openPositionsTd} ${OPEN_POSITION_COL_WIDTHS.symbol}`}>
            <span className="inline-flex items-center gap-1.5 font-semibold tabular-nums tracking-tight">
              <LiveNamedSymbolIcon name={trade.symbol} />
              {trade.symbol}
            </span>
          </TableCell>

          <TableCell className={`${openPositionsTd} ${OPEN_POSITION_COL_WIDTHS.direction}`}>
            <DirectionStatusCell
              status={isPending ? "warn" : "ok"}
              direction={isBuyTrade ? "buy" : "sell"}
            />
          </TableCell>

          <TableCell className={`${openPositionsTd} ${OPEN_POSITION_COL_WIDTHS.lots}`}>
            <span className="font-mono tabular-nums">{trade.lots}</span>
          </TableCell>

          <TableCell className={`${openPositionsTd} ${OPEN_POSITION_COL_WIDTHS.ticket}`}>
            <span className="font-mono tabular-nums text-muted-foreground">
              #{trade.ticket}
            </span>
          </TableCell>

          <TableCell className={`${openPositionsTd} ${OPEN_POSITION_COL_WIDTHS.openPrice}`}>
            <span className="font-mono tabular-nums">
              {trade.openPrice.toFixed(trade.digits)}
            </span>
          </TableCell>

          <TableCell
            className={`${openPositionsTd} ${OPEN_POSITION_COL_WIDTHS.tp} cursor-pointer`}
            title={
              trade.takeProfit > 0
                ? "Focus chart on this trade"
                : "Focus chart and add take profit"
            }
            onClick={(e) => {
              e.stopPropagation();
              focusOpenTradeOnDesktopCharts(trade, {
                activateTpPlacement: !readOnly && !(trade.takeProfit > 0),
              });
            }}
          >
            <span className="font-mono tabular-nums">
              {formatOptionalTradePrice(trade.takeProfit, trade.digits)}
            </span>
          </TableCell>

          <TableCell
            className={`${openPositionsTd} ${OPEN_POSITION_COL_WIDTHS.sl} cursor-pointer`}
            title={
              trade.stopLoss > 0
                ? "Focus chart on this trade"
                : "Focus chart and add stop loss"
            }
            onClick={(e) => {
              e.stopPropagation();
              focusOpenTradeOnDesktopCharts(trade, {
                activateSlPlacement: !readOnly && !(trade.stopLoss > 0),
              });
            }}
          >
            <span className="font-mono tabular-nums">
              {formatOptionalTradePrice(trade.stopLoss, trade.digits)}
            </span>
          </TableCell>

          <OpenPositionLiveCells trade={trade} isMarket={isMarket} />

          <TableCell
            className={`${openPositionsTd} text-right ${OPEN_POSITION_COL_WIDTHS.actions}`}
          >
            {!readOnly && (
            <Button
              variant="ghost"
              size="icon"
              className={DENSITY.ICON_BTN}
              title={actionLabel}
              aria-label={`${actionLabel} #${trade.ticket}`}
              onClick={(e) => {
                e.stopPropagation();
                onCloseTrade(trade);
              }}
            >
              <X className="h-3.5 w-3.5" />
            </Button>
            )}
          </TableCell>
        </TableRow>
      </ContextMenuTrigger>
      {readOnly ? (
      <ContextMenuContent className="w-56">
        <ContextMenuLabel className="text-[11px] font-normal text-muted-foreground">
          {INVESTOR_READ_ONLY_MESSAGE}
        </ContextMenuLabel>
      </ContextMenuContent>
      ) : (
      <ContextMenuContent className="w-48">
        <ContextMenuItem onSelect={() => onCloseTrade(trade)}>
          {actionLabel}
        </ContextMenuItem>
        {isMarket && (
          <>
            <ContextMenuItem onSelect={() => onCloseTrade(trade, trade.lots / 2)}>
              Close ½
              <span className="ml-auto font-mono text-[10px] text-muted-foreground">
                {(trade.lots / 2).toFixed(2)}
              </span>
            </ContextMenuItem>
            <ContextMenuItem onSelect={() => onCloseTrade(trade, trade.lots / 4)}>
              Close ¼
              <span className="ml-auto font-mono text-[10px] text-muted-foreground">
                {(trade.lots / 4).toFixed(2)}
              </span>
            </ContextMenuItem>
            <div
              className="flex items-center gap-1 px-2 py-1"
              onClick={(e) => e.stopPropagation()}
            >
              <Input
                type="number"
                min={0}
                max={trade.lots}
                step={0.01}
                placeholder="Lots"
                value={partialLotsInput}
                onChange={(e) => setPartialLotsInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") submitPartialClose();
                  e.stopPropagation();
                }}
                className="h-6 w-20 text-[11px]"
              />
              <Button
                variant="outline"
                size="sm"
                className="h-6 px-2 text-[11px]"
                disabled={
                  !Number.isFinite(parseFloat(partialLotsInput)) ||
                  parseFloat(partialLotsInput) <= 0
                }
                onClick={submitPartialClose}
              >
                Close
              </Button>
            </div>
          </>
        )}
        <ContextMenuSeparator />
        {/* SL/TP from the row itself — the client could only reach these from the chart, and had no way
            at all to delete a level once set (SR Global, 2026-09-08). */}
        <ContextMenuItem onSelect={() => onModify(trade)}>Modify SL / TP…</ContextMenuItem>
        <ContextMenuItem
          disabled={!(trade.stopLoss > 0)}
          onSelect={() => onClearLevel(trade, "SL")}
        >
          Remove stop loss
          <span className="ml-auto font-mono text-[10px] text-muted-foreground">
            {formatOptionalTradePrice(trade.stopLoss, trade.digits)}
          </span>
        </ContextMenuItem>
        <ContextMenuItem
          disabled={!(trade.takeProfit > 0)}
          onSelect={() => onClearLevel(trade, "TP")}
        >
          Remove take profit
          <span className="ml-auto font-mono text-[10px] text-muted-foreground">
            {formatOptionalTradePrice(trade.takeProfit, trade.digits)}
          </span>
        </ContextMenuItem>
        <ContextMenuSeparator />
        <ContextMenuItem
          disabled={!hasCloseByCandidate}
          onSelect={() => onCloseBy(trade)}
        >
          Close By Hedge
        </ContextMenuItem>
        <ContextMenuSeparator />
        <ContextMenuLabel className="text-[10px] uppercase tracking-wide text-muted-foreground">
          Bulk
        </ContextMenuLabel>
        <ContextMenuItem
          className="text-red-400 focus:text-red-400"
          disabled={bulkCounts.all === 0}
          onSelect={() => onBulkClose("all")}
        >
          Close all positions
          <span className="ml-auto font-mono text-[10px] text-muted-foreground">
            {bulkCounts.all}
          </span>
        </ContextMenuItem>
        <ContextMenuItem
          className="text-red-400 focus:text-red-400"
          disabled={bulkCounts.profitable === 0}
          onSelect={() => onBulkClose("profitable")}
        >
          Close profitable
          <span className="ml-auto font-mono text-[10px] text-muted-foreground">
            {bulkCounts.profitable}
          </span>
        </ContextMenuItem>
        <ContextMenuItem
          className="text-red-400 focus:text-red-400"
          disabled={bulkCounts.losing === 0}
          onSelect={() => onBulkClose("losing")}
        >
          Close losing
          <span className="ml-auto font-mono text-[10px] text-muted-foreground">
            {bulkCounts.losing}
          </span>
        </ContextMenuItem>
      </ContextMenuContent>
      )}
    </ContextMenu>
  );
}

function tradeStaticFieldsEqual(
  a: OpenedTrade,
  b: OpenedTrade
): boolean {
  return (
    a.ticket === b.ticket &&
    a.symbol === b.symbol &&
    a.lots === b.lots &&
    a.openPrice === b.openPrice &&
    a.takeProfit === b.takeProfit &&
    a.stopLoss === b.stopLoss &&
    a.orderType === b.orderType &&
    a.digits === b.digits
  );
}

function propsAreEqual(
  prev: OpenPositionTableRowProps,
  next: OpenPositionTableRowProps
): boolean {
  return (
    tradeStaticFieldsEqual(prev.row.trade, next.row.trade) &&
    prev.row.tradeType === next.row.tradeType &&
    prev.row.isMarket === next.row.isMarket &&
    prev.row.isPending === next.row.isPending &&
    prev.isSelected === next.isSelected &&
    prev.actionLabel === next.actionLabel &&
    prev.hasCloseByCandidate === next.hasCloseByCandidate &&
    prev.bulkCounts.all === next.bulkCounts.all &&
    prev.bulkCounts.profitable === next.bulkCounts.profitable &&
    prev.bulkCounts.losing === next.bulkCounts.losing &&
    prev.onCloseTrade === next.onCloseTrade &&
    prev.onCloseBy === next.onCloseBy &&
    prev.onBulkClose === next.onBulkClose &&
    prev.onModify === next.onModify &&
    prev.onClearLevel === next.onClearLevel &&
    prev.readOnly === next.readOnly
  );
}

export const OpenPositionTableRow = memo(
  OpenPositionTableRowInner,
  propsAreEqual
);
